import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Buildings } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { toast } from "sonner";

export default function OEMs() {
  const { user } = useAuth();
  const canManage = hasRole(user, "super_admin", "hr");
  const canDelete = hasRole(user, "super_admin");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", contact_person: "", contact_email: "", contact_phone: "", website: "", description: "" };
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/oems").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("OEM Name required");
    try {
      if (editing) await api.put(`/oems/${editing.id}`, form);
      else await api.post("/oems", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete OEM "${it.name}"?`)) return;
    try { await api.delete(`/oems/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const filtered = items.filter((i) =>
    !filter || [i.name, i.contact_person, i.contact_email, i.website].some((v) => v && String(v).toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div data-testid="oems-page">
      <PageHeader
        title="OEMs"
        description="Central master of OEMs / brands. Used across Products and Inventory."
        action={canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button className="btn-brand" onClick={() => { setEditing(null); setForm(empty); }} data-testid="oem-new">
                <Plus size={16} className="mr-1.5" /> New OEM
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit OEM" : "New OEM"}</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Field label="OEM / Brand Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="oem-name" /></Field></div>
                <Field label="Contact Person"><Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
                <Field label="Contact Phone"><Input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
                <Field label="Contact Email"><Input value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
                <Field label="Website"><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
                <div className="sm:col-span-2"><Field label="Description"><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="btn-brand" onClick={save} data-testid="oem-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="mb-3">
        <Input placeholder="Filter by name, contact, website…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-md" data-testid="oem-filter" />
      </div>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>OEM</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Website</TableHead>
              <TableHead>Added</TableHead>
              {canManage && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={canManage ? 7 : 6} className="text-center py-10 text-sm opacity-60">No OEMs yet. Click <b>New OEM</b> to add one.</TableCell></TableRow>
            )}
            {filtered.map((it) => (
              <TableRow key={it.id} data-testid={`oem-row-${it.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2"><Buildings size={16} className="text-[#fd7e14]" weight="duotone" /><span className="font-medium">{it.name}</span></div>
                </TableCell>
                <TableCell className="text-sm">{it.contact_person || "—"}</TableCell>
                <TableCell className="text-sm font-mono">{it.contact_phone || "—"}</TableCell>
                <TableCell className="text-sm">{it.contact_email || "—"}</TableCell>
                <TableCell className="text-sm">{it.website ? <a href={it.website.startsWith("http") ? it.website : `https://${it.website}`} target="_blank" rel="noreferrer" className="text-[#fd7e14] hover:underline">{it.website}</a> : "—"}</TableCell>
                <TableCell className="text-xs opacity-70">{fmtDate(it.created_at)}</TableCell>
                {canManage && (
                  <TableCell className="text-right space-x-1.5">
                    <Button size="sm" variant="outline" onClick={() => openEdit(it)}>Edit</Button>
                    {canDelete && <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(it)}>Delete</Button>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
